import { useEffect, useMemo, useState } from 'react'
import {
  AlertCircle,
  Bot,
  Check,
  ChevronLeft,
  ChevronRight,
  Copy,
  Edit,
  FileBarChart,
  Loader2,
  MoreVertical,
  PauseCircle,
  Phone,
  Plus,
  RotateCcw,
  Search,
  Trash2,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import {
  useBatchCalls,
  useCancelBatchCall,
  useDeleteBatchCall,
  useDuplicateBatchCall,
  useGenerateBatchReport,
  useRetryFailedBatchCall,
  type BatchCall,
  type BatchCallStatus,
} from '@/api/batchCalls'
import { usePlanRights } from '@/hooks/usePlanRights'
import { PlanBadge } from '@/components/ui/PlanBadge'

type StatusFilter = 'all' | 'scheduled' | 'in_progress' | 'completed' | 'cancelled'

interface Props {
  onCreate: () => void
  onEdit: (batch: BatchCall) => void
  onOpenDetails: (batch: BatchCall) => void
}

const PAGE_SIZE = 10

const _lot = (id: number, name: string, status: BatchCall['status'], status_display: string, total: number, done: number, answered: number, created_at: string): BatchCall => ({
  id, name, status, status_display, total_calls: total, completed_calls: done, answered_calls: answered,
  no_answer_calls: done - answered, failed_calls: 0, success_rate: done ? Math.round((answered / done) * 100) : 0,
  ai_agent: 1, ai_agent_name: 'Mizou', phone_number: 1, phone_number_display: '+1 514 555-0199', phone_numbers: [],
  context_message: '', timing: 'immediate', scheduled_at: null, retry_enabled: false, retry_at: null, call_delay: 5,
  skip_if_no_appointment: false, no_answer_action: 'sms_client', no_answer_sms_template: '', created_at, updated_at: created_at,
})

const APERCU_LOTS: BatchCall[] = [
  _lot(-1, 'Confirmation des rendez-vous de lundi', 'in_progress', 'En cours', 42, 27, 22, '2026-09-18T14:00:00Z'),
  _lot(-2, 'Rappel de révision', 'completed', 'Terminée', 120, 120, 91, '2026-09-11T14:00:00Z'),
  _lot(-3, 'Relance factures impayées', 'scheduled', 'Planifiée', 35, 0, 0, '2026-09-20T09:00:00Z'),
]

export function BatchCallsListView({ onCreate, onEdit, onOpenDetails }: Props) {
  const { allowed, planFor } = usePlanRights()
  const sortantsPermis = allowed('appels_sortants_agent')
  const { t } = useTranslation()
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const dateLocale = locale === 'fr' ? 'fr-CA' : 'en-CA'

  const { data: vrais = [], isLoading } = useBatchCalls()
  // Sans le plan et sans campagne : aperçu flouté de la page.
  const apercu = !sortantsPermis && vrais.length === 0
  const batches = apercu ? APERCU_LOTS : vrais
  const deleteBatch = useDeleteBatchCall()
  const cancelBatch = useCancelBatchCall()
  const duplicateBatch = useDuplicateBatchCall()
  const retryFailed = useRetryFailedBatchCall()
  const generateReport = useGenerateBatchReport()

  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [pendingDelete, setPendingDelete] = useState<BatchCall | null>(null)
  const [pendingCancel, setPendingCancel] = useState<BatchCall | null>(null)
  const [pendingRetry, setPendingRetry] = useState<BatchCall | null>(null)
  const [reportSent, setReportSent] = useState(false)

  // Reset page quand les filtres changent.
  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery, statusFilter])

  const filtered = useMemo(() => {
    let out = batches
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      out = out.filter(
        (b) =>
          b.name.toLowerCase().includes(q) ||
          b.ai_agent_name.toLowerCase().includes(q),
      )
    }
    if (statusFilter !== 'all') {
      out = out.filter((b) => {
        if (statusFilter === 'in_progress') return b.status.includes('progress')
        if (statusFilter === 'scheduled') return b.status.includes('scheduled')
        return b.status === statusFilter
      })
    }
    return out
  }, [batches, searchQuery, statusFilter])

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const paginated = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  )

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return
    try {
      await deleteBatch.mutateAsync(pendingDelete.id)
      toast.success(t('manager.batchCalls.toast.deleted'))
    } catch {
      toast.error(t('manager.batchCalls.toast.deleteError'))
    } finally {
      setPendingDelete(null)
    }
  }

  const handleConfirmCancel = async () => {
    if (!pendingCancel) return
    try {
      await cancelBatch.mutateAsync(pendingCancel.id)
      toast.success(t('manager.batchCalls.toast.cancelled'))
    } catch {
      toast.error(t('manager.batchCalls.toast.cancelError'))
    } finally {
      setPendingCancel(null)
    }
  }

  const handleConfirmRetry = async () => {
    if (!pendingRetry) return
    try {
      await retryFailed.mutateAsync(pendingRetry.id)
      toast.success(t('manager.batchCalls.toast.retryQueued'))
    } catch {
      toast.error(t('manager.batchCalls.toast.retryError'))
    } finally {
      setPendingRetry(null)
    }
  }

  const handleDuplicate = async (batch: BatchCall) => {
    try {
      await duplicateBatch.mutateAsync(batch.id)
      toast.success(t('manager.batchCalls.toast.duplicated'))
    } catch {
      toast.error(t('manager.batchCalls.toast.duplicateError'))
    }
  }

  const handleGenerateReport = async (
    period: 'current_month' | 'last_month' | 'last_3_months',
  ) => {
    try {
      await generateReport.mutateAsync(period)
      setReportSent(true)
      setTimeout(() => setReportSent(false), 5000)
    } catch {
      toast.error(t('manager.batchCalls.toast.reportError'))
    }
  }

  const formatDate = (iso: string | null) => {
    if (!iso) return '—'
    const d = new Date(iso)
    return (
      d.toLocaleDateString(dateLocale, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      }) +
      ', ' +
      d.toLocaleTimeString(dateLocale, { hour: '2-digit', minute: '2-digit' })
    )
  }

  const getStatusBadge = (status: BatchCallStatus) => {
    const config: Record<
      string,
      { bg: string; text: string; dot: string; pulse?: boolean }
    > = {
      draft: {
        bg: 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700',
        text: 'text-slate-700 dark:text-slate-300',
        dot: 'bg-slate-400',
      },
      scheduled: {
        bg: 'bg-blue-50 dark:bg-blue-500/10 border-blue-200 dark:border-blue-500/30',
        text: 'text-blue-700 dark:text-blue-300',
        dot: 'bg-blue-500',
      },
      in_progress: {
        bg: 'bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/30',
        text: 'text-amber-700 dark:text-amber-300',
        dot: 'bg-amber-500',
        pulse: true,
      },
      retry_scheduled: {
        bg: 'bg-purple-50 dark:bg-purple-500/10 border-purple-200 dark:border-purple-500/30',
        text: 'text-purple-700 dark:text-purple-300',
        dot: 'bg-purple-500',
      },
      retry_in_progress: {
        bg: 'bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/30',
        text: 'text-amber-700 dark:text-amber-300',
        dot: 'bg-amber-500',
        pulse: true,
      },
      completed: {
        bg: 'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/30',
        text: 'text-emerald-700 dark:text-emerald-300',
        dot: 'bg-emerald-500',
      },
      cancelled: {
        bg: 'bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-500/30',
        text: 'text-red-700 dark:text-red-300',
        dot: 'bg-red-500',
      },
    }
    const c = config[status] ?? config.draft
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium border',
          c.bg,
          c.text,
        )}
      >
        <span
          className={cn('w-1.5 h-1.5 rounded-full', c.dot, c.pulse && 'animate-pulse')}
        />
        {t(`manager.batchCalls.statuses.${status}`, { defaultValue: status })}
      </span>
    )
  }

  const getProgressBar = (batch: BatchCall) => {
    const rate = batch.success_rate
    if (
      batch.status === 'scheduled' ||
      batch.status === 'draft' ||
      batch.total_calls === 0
    ) {
      return (
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-400 w-9">—</span>
          <div className="w-24 h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
            <div className="h-full bg-slate-300 dark:bg-slate-600 rounded-full" />
          </div>
        </div>
      )
    }
    const barColor =
      rate >= 70 ? 'bg-emerald-500' : rate >= 40 ? 'bg-amber-500' : 'bg-red-500'
    return (
      <div className="flex items-center gap-3">
        <span className="text-sm text-slate-600 dark:text-slate-400 w-9 tabular-nums">
          {rate.toFixed(0)}%
        </span>
        <div className="w-24 h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
          <div className={cn('h-full rounded-full', barColor)} style={{ width: `${rate}%` }} />
        </div>
      </div>
    )
  }

  const filterButtons: { key: StatusFilter; label: string }[] = [
    { key: 'all', label: t('manager.batchCalls.filters.all') },
    { key: 'scheduled', label: t('manager.batchCalls.filters.scheduled') },
    { key: 'in_progress', label: t('manager.batchCalls.filters.inProgress') },
    { key: 'completed', label: t('manager.batchCalls.filters.completed') },
    { key: 'cancelled', label: t('manager.batchCalls.filters.cancelled') },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">
            {t('manager.batchCalls.sectionTitle')}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {t('manager.batchCalls.sectionSubtitle')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="text-sm gap-2">
                {reportSent ? (
                  <>
                    <Check className="w-4 h-4 text-emerald-600" />
                    <span className="text-emerald-600">
                      {t('manager.batchCalls.report.sentByEmail')}
                    </span>
                  </>
                ) : (
                  <>
                    <FileBarChart className="w-4 h-4" />
                    {t('manager.batchCalls.report.globalReport')}
                  </>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem
                onClick={() => void handleGenerateReport('current_month')}
              >
                {t('manager.batchCalls.report.currentMonth')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => void handleGenerateReport('last_month')}
              >
                {t('manager.batchCalls.report.lastMonth')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => void handleGenerateReport('last_3_months')}
              >
                {t('manager.batchCalls.report.last3Months')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {!sortantsPermis && <PlanBadge plan={planFor('appels_sortants_agent')} />}
          <Button
            type="button"
            onClick={onCreate}
            disabled={!sortantsPermis}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('manager.batchCalls.newCampaign')}
          </Button>
        </div>
      </div>

      {/* Filtres */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative w-72 max-w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
          <Input
            placeholder={t('manager.batchCalls.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-9 text-sm bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700"
          />
        </div>
        <div className="flex items-center bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md overflow-hidden">
          {filterButtons.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setStatusFilter(f.key)}
              className={cn(
                'px-3 py-1.5 text-sm font-medium transition-colors border-r border-slate-200 dark:border-slate-700 last:border-r-0',
                statusFilter === f.key
                  ? 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/50',
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Liste */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-12 text-center">
          <Phone className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
            {t('manager.batchCalls.empty.title')}
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-4 max-w-sm mx-auto">
            {t('manager.batchCalls.empty.subtitle')}
          </p>
          <div className="flex flex-col items-center gap-3">
          {!sortantsPermis && <PlanBadge plan={planFor('appels_sortants_agent')} />}
          <Button
            type="button"
            onClick={onCreate}
            disabled={!sortantsPermis}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('manager.batchCalls.empty.create')}
          </Button>
        </div>
        </div>
      ) : (
        <div className="relative">
          {apercu && (
            <div className="absolute inset-0 z-10 flex items-start justify-center pt-16">
              <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-10 py-8 text-center shadow-xl">
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                  {t('plan.required', { plan: 'Business' })}
                </p>
                <PlanBadge plan={planFor('appels_sortants_agent')} />
              </div>
            </div>
          )}
          <div
            className={cn(
              'rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm overflow-hidden overflow-x-auto',
              apercu && 'blur-[3px] opacity-70 pointer-events-none select-none',
            )}
            aria-hidden={apercu || undefined}
          >
            <table className="w-full text-left border-collapse whitespace-nowrap">
              <thead className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800">
                <tr>
                  <th className="px-6 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t('manager.batchCalls.table.name')}
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t('manager.batchCalls.table.aiAgent')}
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t('manager.batchCalls.table.numbers')}
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t('manager.batchCalls.table.scheduledFor')}
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t('manager.batchCalls.table.status')}
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t('manager.batchCalls.table.successRate')}
                  </th>
                  <th className="px-6 py-3 w-10" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {paginated.map((batch) => (
                  <tr
                    key={batch.id}
                    onClick={() => onOpenDetails(batch)}
                    className={cn(
                      'group hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer',
                      batch.status === 'cancelled' && 'opacity-60',
                    )}
                  >
                    <td className="px-6 py-2.5">
                      <span
                        className={cn(
                          'text-sm font-medium',
                          batch.status === 'cancelled'
                            ? 'line-through text-slate-400'
                            : 'text-slate-900 dark:text-white',
                        )}
                      >
                        {batch.name}
                      </span>
                    </td>
                    <td className="px-6 py-2.5">
                      <div className="flex items-center gap-2">
                        <Bot className="w-4 h-4 text-slate-400" />
                        <span className="text-sm text-slate-600 dark:text-slate-400">
                          {batch.ai_agent_name}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-2.5 text-sm text-slate-600 dark:text-slate-400 tabular-nums">
                      {batch.total_calls}
                    </td>
                    <td className="px-6 py-2.5 text-sm text-slate-600 dark:text-slate-400">
                      {formatDate(batch.scheduled_at)}
                    </td>
                    <td className="px-6 py-2.5">{getStatusBadge(batch.status)}</td>
                    <td className="px-6 py-2.5">{getProgressBar(batch)}</td>
                    <td
                      className="px-6 py-2.5 text-right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          {batch.status === 'draft' && (
                            <DropdownMenuItem onClick={() => onEdit(batch)}>
                              <Edit className="w-4 h-4 mr-2" />
                              {t('manager.batchCalls.actions.edit')}
                            </DropdownMenuItem>
                          )}
                          {!batch.status.includes('progress') && (
                            <DropdownMenuItem
                              onClick={() => void handleDuplicate(batch)}
                            >
                              <Copy className="w-4 h-4 mr-2" />
                              {t('manager.batchCalls.actions.duplicate')}
                            </DropdownMenuItem>
                          )}
                          {batch.status === 'completed' &&
                            (batch.no_answer_calls > 0 ||
                              batch.failed_calls > 0) && (
                              <DropdownMenuItem onClick={() => setPendingRetry(batch)}>
                                <RotateCcw className="w-4 h-4 mr-2 text-orange-600" />
                                {t('manager.batchCalls.actions.retry')}
                              </DropdownMenuItem>
                            )}
                          {(batch.status === 'scheduled' ||
                            batch.status.includes('progress')) && (
                            <DropdownMenuItem onClick={() => setPendingCancel(batch)}>
                              <PauseCircle className="w-4 h-4 mr-2" />
                              {t('manager.batchCalls.actions.cancel')}
                            </DropdownMenuItem>
                          )}
                          {(batch.status === 'draft' ||
                            batch.status === 'completed' ||
                            batch.status === 'cancelled') && (
                            <DropdownMenuItem
                              onClick={() => setPendingDelete(batch)}
                              className="text-red-600 focus:text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              {t('manager.batchCalls.actions.delete')}
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-end gap-4">
              <span className="text-sm text-slate-500 dark:text-slate-400">
                {(currentPage - 1) * PAGE_SIZE + 1}-
                {Math.min(currentPage * PAGE_SIZE, filtered.length)}{' '}
                {t('manager.batchCalls.pagination.of')} {filtered.length}
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="w-8 h-8 flex items-center justify-center rounded text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="w-8 h-8 flex items-center justify-center rounded text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Confirm retry */}
      <AlertDialog
        open={!!pendingRetry}
        onOpenChange={(o) => !o && setPendingRetry(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-orange-600" />
              {t('manager.batchCalls.retryDialog.title')}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 pt-2">
                <p>{t('manager.batchCalls.retryDialog.description')}</p>
                <ul className="list-disc list-inside space-y-1 text-sm bg-slate-50 dark:bg-slate-800 p-3 rounded-md">
                  <li>
                    {t('manager.batchCalls.retryDialog.noAnswer', {
                      count: pendingRetry?.no_answer_calls ?? 0,
                    })}
                  </li>
                  <li>
                    {t('manager.batchCalls.retryDialog.failed', {
                      count: pendingRetry?.failed_calls ?? 0,
                    })}
                  </li>
                </ul>
                <p className="text-xs text-slate-500 italic">
                  {t('manager.batchCalls.retryDialog.info')}
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={retryFailed.isPending}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                void handleConfirmRetry()
              }}
              disabled={retryFailed.isPending}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              {t('manager.batchCalls.retryDialog.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm cancel */}
      <AlertDialog
        open={!!pendingCancel}
        onOpenChange={(o) => !o && setPendingCancel(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <PauseCircle className="h-5 w-5 text-orange-600" />
              {t('manager.batchCalls.cancelDialog.title')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('manager.batchCalls.cancelDialog.description', {
                name: pendingCancel?.name,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelBatch.isPending}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                void handleConfirmCancel()
              }}
              disabled={cancelBatch.isPending}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              {t('manager.batchCalls.cancelDialog.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm delete */}
      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-600" />
              {t('manager.batchCalls.deleteDialog.title')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('manager.batchCalls.deleteDialog.description', {
                name: pendingDelete?.name,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteBatch.isPending}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                void handleConfirmDelete()
              }}
              disabled={deleteBatch.isPending}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {t('manager.batchCalls.deleteDialog.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
